import React, { createRef, forwardRef, Key, ReactElement, ReactFragment, ReactPortal, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { StepProps, StepsWindowStep } from './StepsWindowStep';
import { Window, WindowProps } from './Window';

export const StepsWindow = forwardRef<NavigateRef, Props>(({ children, close, ...rest }, ref) => {

    const steps = useMemo(() => {
        return React.Children.toArray(children).filter(Boolean) as StepElement[];
    }, [children]);

    const keys = steps.map(c => c.key);
    const [key, setKey] = useState<Key | null>(keys[0]);

    const index = keys.indexOf(key);
    const setIndex = useCallback((index: number) => {
        setKey(keys[index]);
    }, [keys]);

    useImperativeHandle(ref, () => ({
        navigateTo: setIndex
    }), [setIndex]);

    const forward = useCallback(() => {
        if (index < steps.length - 1) {
            setIndex(index + 1);
        } else {
            close();
        }
    }, [index, steps.length, setIndex, close]);

    const back = useCallback(() => {
        if (index > 0) {
            setIndex(index - 1);
        } else {
            close();
        }
    }, [close, index, setIndex]);

    const Step = addNavigationToStep(steps[index], forward, back, close);

    return (
        <Window close={close} {...rest}>
            {Step}
        </Window>
    );
});

export type NavigateRef = { navigateTo: (index: number) => void };

export const useStepsWindowNavigation = () => {
    const ref = createRef<NavigateRef>();
    const navigateTo = useCallback((index: number) => {
        ref.current?.navigateTo(index);
    }, [ref]);
    return { ref, navigateTo };
};

type StepElement = ReactElement<StepProps, typeof StepsWindowStep>;
type Child = StepElement | undefined | false;

type Props = WindowProps & {
    children: Child[];
};

const addNavigationToStep = (
    step: React.ReactChild | ReactFragment | ReactPortal,
    forward: () => void,
    back: () => void,
    close: () => void
) => {

    if (!React.isValidElement(step)) {
        throw new Error('Invalid Step provided');
    }

    return React.cloneElement(step, getNavigationProps(step.props, forward, back, close));
};

const getNavigationProps = (
    props: StepProps,
    goForward: () => void,
    goBack: () => void,
    closeWindow: () => void
): Partial<StepProps> => ({

    onForward: () => {
        props.onForward && props.onForward();
        goForward();
    },

    onBack: () => {
        props.onBack && props.onBack();
        goBack();
    },

    onClose: () => {
        props.onClose && props.onClose();
        closeWindow();
    }
});